/**
 * F3 performance overlay.
 *
 * Shows fps, the rolling 1 %-low, the per-subsystem millisecond breakdown, draw calls,
 * triangles, JS heap, the renderer backend and the quality tier — the numbers the budgets in
 * CLAUDE.md are written in, so a regression is visible without leaving the sim.
 *
 * Cost control: the DOM structure is built once and only `textContent`/`className` are touched
 * afterwards, at 5 Hz rather than every frame, and only while the overlay is visible. Hidden,
 * it costs one boolean check per frame.
 *
 * `updateFrame(alpha)`: called from the loop's after-frame slot, self-throttled.
 */

import { PRODUCT_NAME } from '../brand.js';
import { CHANNEL_BUDGET_MS, Channel, type Profiler, heapUsedBytes } from '../core/profiler.js';
import type { QualityGovernor } from '../core/governor.js';
import type { QualityManager } from '../core/quality.js';
import type { RendererBackend } from '../render/renderer.js';
import type { TerrainStats } from '../world/terrain/terrainSystem.js';

/** Refresh rate of the readout [Hz]. Fast enough to feel live, slow enough to be readable. */
const REFRESH_HZ = 5;

export interface RenderStats {
  drawCalls: number;
  triangles: number;
  widthPx: number;
  heightPx: number;
}

interface Row {
  value: HTMLSpanElement;
}

export interface PerfOverlayOptions {
  profiler: Profiler;
  quality: QualityManager;
  governor: QualityGovernor;
  backend: RendererBackend;
  forcedFallback: boolean;
  parent?: HTMLElement;
}

export class PerfOverlay {
  readonly element: HTMLDivElement;

  private readonly profiler: Profiler;
  private readonly quality: QualityManager;
  private readonly governor: QualityGovernor;
  private readonly rows = new Map<string, Row>();
  private readonly stats: RenderStats = { drawCalls: 0, triangles: 0, widthPx: 0, heightPx: 0 };
  private terrain: Readonly<TerrainStats> | null = null;

  private nextUpdateMs = 0;
  private visibleValue = false;
  private heapAtStartBytes = 0;

  constructor(options: PerfOverlayOptions) {
    this.profiler = options.profiler;
    this.quality = options.quality;
    this.governor = options.governor;
    this.heapAtStartBytes = heapUsedBytes();

    this.element = document.createElement('div');
    this.element.className = 'perf';
    this.element.hidden = true;

    const title = document.createElement('div');
    title.className = 'perf__title';
    const titleLeft = document.createElement('span');
    titleLeft.textContent = PRODUCT_NAME;
    const titleRight = document.createElement('span');
    titleRight.textContent = options.forcedFallback
      ? `${options.backend} (forced)`
      : options.backend;
    title.append(titleLeft, titleRight);
    this.element.append(title);

    this.addRow('fps', 'fps');
    this.addRow('low', '1% low (5s)');
    this.addRow('frame', 'frame');
    this.addRow('physics', '  physics', true);
    this.addRow('terrain', '  terrain');
    this.addRow('renderCPU', '  render cpu');
    this.addRow('audio', '  audio');
    this.addRow('draws', 'draw calls', true);
    this.addRow('tris', 'triangles');
    this.addRow('res', 'resolution');
    this.addRow('tiles', 'tiles vis/res', true);
    this.addRow('stream', 'streaming');
    this.addRow('tilecache', 'tile cache');
    this.addRow('worker', 'worker f/d/m');
    this.addRow('heap', 'js heap', true);
    this.addRow('tier', 'quality');
    this.addRow('auto', '  governor');
    this.addRow('ticks', 'physics ticks');

    const hint = document.createElement('div');
    hint.className = 'perf__hint';
    hint.textContent = 'F3 overlay · P pause · drag orbit · wheel zoom';
    this.element.append(hint);

    (options.parent ?? document.body).append(this.element);
  }

  private addRow(key: string, label: string, startsGroup = false): void {
    const row = document.createElement('div');
    row.className = startsGroup ? 'perf__row perf__row--group' : 'perf__row';

    const labelEl = document.createElement('span');
    labelEl.className = 'perf__label';
    labelEl.textContent = label;

    const valueEl = document.createElement('span');
    valueEl.className = 'perf__value';
    valueEl.textContent = '—';

    row.append(labelEl, valueEl);
    this.element.append(row);
    this.rows.set(key, { value: valueEl });
  }

  private set(key: string, text: string, severity: 'good' | 'warn' | 'bad' | 'none' = 'none'): void {
    const row = this.rows.get(key);
    if (row === undefined) return;
    if (row.value.textContent !== text) row.value.textContent = text;
    const className = severity === 'none' ? 'perf__value' : `perf__value perf__value--${severity}`;
    if (row.value.className !== className) row.value.className = className;
  }

  get visible(): boolean {
    return this.visibleValue;
  }

  toggle(): void {
    this.setVisible(!this.visibleValue);
  }

  setVisible(visible: boolean): void {
    this.visibleValue = visible;
    this.element.hidden = !visible;
    this.nextUpdateMs = 0; // refresh immediately on open
  }

  /** Hand the overlay the renderer's per-frame counters. Cheap enough to call every frame. */
  setRenderStats(drawCalls: number, triangles: number, widthPx: number, heightPx: number): void {
    this.stats.drawCalls = drawCalls;
    this.stats.triangles = triangles;
    this.stats.widthPx = widthPx;
    this.stats.heightPx = heightPx;
  }

  /**
   * Hand the overlay the terrain streamer's counters.
   *
   * These are the numbers the milestone's acceptance criteria are written in — "tile loads
   * cause zero frame hitches > 4 ms (prove it with the F3 breakdown)" — so the worker's own
   * fetch/decode/mesh split is on screen next to the main thread's terrain channel. If the
   * terrain channel is flat while the worker figures are large, the work really is off the
   * frame path.
   */
  setTerrainStats(stats: Readonly<TerrainStats>): void {
    this.terrain = stats;
  }

  /** Reset the heap baseline — used by the perf harness to report a delta over its run. */
  markHeapBaseline(): void {
    this.heapAtStartBytes = heapUsedBytes();
  }

  /** Called once per frame; does real work only ~5 times a second, and only when visible. */
  updateFrame(nowMs: number, totalTicks: number): void {
    if (!this.visibleValue || nowMs < this.nextUpdateMs) return;
    this.nextUpdateMs = nowMs + 1000 / REFRESH_HZ;

    const profiler = this.profiler;
    const fps = profiler.averageFps;
    const lowFps = profiler.lowFps();

    this.set('fps', fps.toFixed(1), fps >= 60 ? 'good' : fps >= 45 ? 'warn' : 'bad');
    this.set('low', lowFps.toFixed(1), lowFps >= 55 ? 'good' : lowFps >= 40 ? 'warn' : 'bad');
    this.set('frame', `${profiler.averageFrameMs.toFixed(2)} ms`);

    this.setChannel('physics', Channel.Physics);
    this.setChannel('terrain', Channel.Terrain);
    this.setChannel('renderCPU', Channel.RenderCPU);
    this.setChannel('audio', Channel.Audio);

    const draws = this.stats.drawCalls;
    this.set('draws', String(draws), draws < 350 ? 'good' : draws < 500 ? 'warn' : 'bad');
    this.set('tris', formatCount(this.stats.triangles));
    this.set('res', `${this.stats.widthPx}×${this.stats.heightPx}`);

    const heapBytes = heapUsedBytes();
    if (heapBytes > 0) {
      const deltaMb = (heapBytes - this.heapAtStartBytes) / (1024 * 1024);
      const sign = deltaMb >= 0 ? '+' : '−';
      this.set(
        'heap',
        `${(heapBytes / (1024 * 1024)).toFixed(1)} MB  ${sign}${Math.abs(deltaMb).toFixed(1)}`,
      );
    } else {
      this.set('heap', 'n/a'); // only Chrome exposes performance.memory
    }

    const terrain = this.terrain;
    if (terrain !== null) {
      this.set('tiles', `${terrain.selected} / ${terrain.resident}`);
      this.set(
        'stream',
        `${terrain.loading} load · ${terrain.queued} q · ${terrain.inFlight} net`,
        terrain.loading > 60 ? 'warn' : 'good',
      );
      const usedMb = terrain.cacheBytes / (1024 * 1024);
      const maxMb = terrain.cacheMaxBytes / (1024 * 1024);
      this.set(
        'tilecache',
        `${usedMb.toFixed(0)}/${maxMb.toFixed(0)} MB · ${terrain.evictions} ev`,
        usedMb <= maxMb ? 'good' : 'bad',
      );
      this.set(
        'worker',
        `${terrain.lastFetchMs.toFixed(0)}/${terrain.lastDecodeMs.toFixed(1)}/${terrain.lastMeshMs.toFixed(1)} ms`,
      );
    }

    this.set('tier', this.tierText(nowMs));
    this.set('auto', this.autoText(nowMs));
    this.set('ticks', formatCount(totalTicks));
  }

  private tierText(nowMs: number): string {
    if (this.quality.mode !== 'auto') return `${this.quality.tier} (locked)`;
    return this.governor.isWarmingUp(nowMs)
      ? `${this.quality.tier} (auto, warming up)`
      : `${this.quality.tier} (auto)`;
  }

  /**
   * What the Auto governor is currently thinking, in one line.
   *
   * Worth the row: a governor that is stuck is invisible without it — the milestone-1 test
   * flight spent a whole session on "low (auto)" with no way to see that the upgrade dwell was
   * being reset faster than it could accumulate, or what threshold it was even steering to.
   */
  private autoText(nowMs: number): string {
    if (this.quality.mode !== 'auto') return '—';
    const rule = this.governor.pendingRule();
    if (rule === 'none') return `holding · up > ${this.governor.currentUpgradeFps.toFixed(0)}`;
    const arrow = rule === 'down' ? '↓' : '↑';
    const target = rule === 'down' ? this.governor.downgradeBelowFps : this.governor.currentUpgradeFps;
    return (
      `${arrow} ${this.governor.pendingSeconds(nowMs).toFixed(1)}` +
      `/${this.governor.pendingTargetSeconds().toFixed(0)}s @ ${target.toFixed(0)}`
    );
  }

  private setChannel(key: string, channel: (typeof Channel)[keyof typeof Channel]): void {
    const ms = this.profiler.channelSmoothedMs(channel);
    const budget = CHANNEL_BUDGET_MS[channel];
    const severity = ms <= budget ? 'good' : ms <= budget * 1.5 ? 'warn' : 'bad';
    this.set(key, `${ms.toFixed(2)} / ${budget.toFixed(1)} ms`, severity);
  }

  dispose(): void {
    this.element.remove();
  }
}

function formatCount(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return String(Math.round(value));
}
